import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthState, getAuthState } from '@/types/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

export const AuthGuard = ({ 
  children, 
  requireAuth = true, 
  redirectTo = '/auth',
  fallback 
}: AuthGuardProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const authState = getAuthState(user, loading);

  React.useEffect(() => {
    if (!(requireAuth && authState === AuthState.UNAUTHENTICATED)) return;

    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');
    const error = urlParams.get('error');

    if (connected || error) {
      // Platform-OAuth return — let the auth page handle the params.
      navigate('/auth' + window.location.search);
      return;
    }

    // Supabase auth response in the URL? PKCE puts the code in the query
    // string; the legacy implicit flow puts an access token in the hash.
    // supabase-js auto-exchanges either, but the SIGNED_IN event hasn't
    // necessarily fired yet by the time we land here. Wait briefly so we
    // don't bounce a user out mid-exchange.
    const hasPendingAuth =
      window.location.search.includes('code=') ||
      window.location.hash.includes('access_token=');

    if (hasPendingAuth) {
      const timer = setTimeout(() => {
        navigate(redirectTo);
      }, 3000);
      return () => clearTimeout(timer);
    }

    navigate(redirectTo);
  }, [authState, requireAuth, redirectTo, navigate]);

  if (authState === AuthState.LOADING) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-6 h-6 bg-gradient-to-br from-primary to-muted rounded-sm rotate-45 mx-auto mb-4 animate-pulse"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && authState === AuthState.UNAUTHENTICATED) {
    return fallback || (
      <Card className="max-w-md mx-auto mt-8">
        <CardContent className="text-center py-8">
          <h3 className="text-lg font-medium mb-2">Authentication Required</h3>
          <p className="text-muted-foreground mb-4">Please sign in to continue</p>
          <Button onClick={() => navigate(redirectTo)}>Sign In</Button>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
};

export default AuthGuard;